import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { HStack } from '@chakra-ui/react';
import React, { createContext, forwardRef, useCallback, useContext, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
export const FIELD_HIGHLIGHTER_CLASSNAMES = {
    JAEN_HIGHLIGHT_FRAME: 'jaen-highlight-frame',
    JAEN_HIGHLIGHT_TOOLTIP: 'jaen-highlight-tooltip'
};
export const FieldHighlighterProviderContext = createContext({
    ref: () => { }
});
const Tooltip = forwardRef((props, ref) => {
    return (_jsx(HStack, { ref: ref, id: "momo", w: "fit-content", mt: "-6", p: "1", mx: "auto", justifyContent: "center", gap: "1", pointerEvents: "all", children: props.actions.map((action, index) => (_jsx(React.Fragment, { children: action }, index))) }));
});
export const FieldHighlighterProvider = props => {
    const fields = useRef([]);
    useEffect(() => {
        fields.current = [];
        hideHighlightRef.current();
    }, [props.path]);
    const tooltipHightRef = useRef(null);
    const [tooltipButtons, setTooltipButtons] = React.useState([]);
    const frameRootRef = useRef(null);
    const [tooltipRoot, setTooltipRoot] = React.useState(null);
    const resizeObserver = useRef(null);
    const scrollAt = useRef({ left: 0, top: 0 });
    const hideHighlightRef = useRef(() => { });
    const ensureRoots = () => {
        const appRoot = document.getElementById('___gatsby');
        if (!appRoot) {
            alert('appRoot root not found, please contact support');
            return null;
        }
        let frameRoot = frameRootRef.current;
        if (!frameRoot || !frameRoot.isConnected) {
            frameRoot = appRoot.appendChild(document.createElement('div'));
            frameRoot.classList.add(FIELD_HIGHLIGHTER_CLASSNAMES.JAEN_HIGHLIGHT_FRAME);
            frameRoot.style.position = 'absolute';
            const tooltipRootNode = frameRoot.appendChild(document.createElement('div'));
            tooltipRootNode.classList.add(FIELD_HIGHLIGHTER_CLASSNAMES.JAEN_HIGHLIGHT_TOOLTIP);
            tooltipRootNode.style.position = 'sticky';
            tooltipRootNode.style.top = `3.5rem`;
            tooltipRootNode.style.pointerEvents = 'none';
            tooltipRootNode.style.zIndex = '999';
            tooltipRootNode.style.width = '100%';
            tooltipRootNode.tabIndex = -1;
            frameRootRef.current = frameRoot;
            setTooltipRoot(tooltipRootNode);
        }
        return frameRoot;
    };
    const setHighlight = (element) => {
        const frameRoot = ensureRoots();
        if (!frameRoot)
            return;
        frameRoot.style.display = '';
        scrollAt.current = {
            left: window.pageXOffset || document.documentElement.scrollLeft,
            top: window.pageYOffset || document.documentElement.scrollTop
        };
        if (!resizeObserver.current) {
            resizeObserver.current = new ResizeObserver(entries => {
                const entry = entries[0];
                if (!entry)
                    return;
                const elementRect = entry.target.getBoundingClientRect();
                const root = frameRootRef.current;
                if (!root)
                    return;
                root.style.top = `${elementRect.top + scrollAt.current.top}px`;
                root.style.left = `${elementRect.left + scrollAt.current.left}px`;
                root.style.width = `${elementRect.width}px`;
                root.style.height = `${elementRect.height}px`;
            });
        }
        resizeObserver.current.disconnect();
        resizeObserver.current.observe(element);
        const field = fields.current.find(item => item.ref === element);
        setTooltipButtons(field?.tooltipButtons ?? []);
    };
    const hideHighlight = () => {
        const frameRoot = frameRootRef.current;
        if (frameRoot) {
            frameRoot.style.display = 'none';
        }
        resizeObserver.current?.disconnect();
    };
    hideHighlightRef.current = hideHighlight;
    const findClosestParentMatching = (element, find) => {
        let currentElement = element;
        let index = -1;
        while (currentElement) {
            index++;
            if (find(currentElement)) {
                return { element: currentElement, index };
            }
            currentElement = currentElement.parentElement;
        }
        return null;
    };
    const mouseEnterHandler = useCallback((e) => {
        const element = e.target;
        element.focus({
            preventScroll: true
        });
    }, []);
    const mouseLeaveHandler = useCallback((e) => {
        const relatedTarget = e.relatedTarget;
        const nextItem = Object.values(fields.current).find(item => item.ref === relatedTarget);
        if (nextItem) {
            nextItem?.ref?.focus({
                preventScroll: true
            });
        }
        else {
            const closestParent = findClosestParentMatching(relatedTarget, element => fields.current.some(item => item.ref === element));
            if (closestParent) {
                const closestItem = fields.current.find(item => item.ref === closestParent.element);
                closestItem?.ref?.focus({
                    preventScroll: true
                });
            }
        }
    }, []);
    const focusHandler = useCallback((e) => {
        const element = e.target;
        if (element.isContentEditable) {
            const range = document.createRange();
            const selection = window.getSelection();
            if (!selection)
                return;
            range.selectNodeContents(element);
            range.collapse(false);
            selection.removeAllRanges();
            selection.addRange(range);
        }
        setHighlight(element);
    }, []);
    const blurHandler = useCallback((e) => {
        const highlightRoot = frameRootRef.current;
        if (!highlightRoot || highlightRoot.style.display === 'none')
            return;
        if (tooltipHightRef.current?.contains(e.relatedTarget)) {
            return;
        }
        const element = e.currentTarget;
        if (element?.contains(e.relatedTarget)) {
            return;
        }
        if (highlightRoot.contains(e.relatedTarget)) {
            return;
        }
        hideHighlight();
    }, []);
    const ref = useCallback((ref, tooltipButtons) => {
        if (ref) {
            const fieldIndex = fields.current.findIndex(item => item.ref === ref);
            const field = fields.current[fieldIndex];
            if (field?.ref) {
                field.ref.removeEventListener('mouseenter', mouseEnterHandler);
                field.ref.removeEventListener('mouseleave', mouseLeaveHandler);
                field.ref.removeEventListener('focus', focusHandler);
                field.ref.removeEventListener('blur', blurHandler);
            }
            ref.addEventListener('mouseenter', mouseEnterHandler);
            ref.addEventListener('mouseleave', mouseLeaveHandler);
            ref.addEventListener('focus', focusHandler);
            ref.addEventListener('blur', blurHandler);
            if (fieldIndex !== -1) {
                fields.current[fieldIndex] = {
                    ref,
                    tooltipButtons
                };
            }
            else {
                fields.current.push({
                    ref,
                    tooltipButtons
                });
            }
        }
    }, [
        mouseEnterHandler,
        mouseLeaveHandler,
        focusHandler,
        blurHandler
    ]);
    const portaledTooltip = React.useMemo(() => {
        if (!tooltipRoot)
            return null;
        return createPortal(_jsx(Tooltip, { actions: tooltipButtons, ref: tooltipHightRef }), tooltipRoot);
    }, [tooltipRoot, tooltipButtons]);
    const contextValue = React.useMemo(() => ({ ref }), [ref]);
    return (_jsxs(FieldHighlighterProviderContext.Provider, { value: contextValue, children: [portaledTooltip, props.children] }));
};
export const useHighlight = ({ tooltipButtons }) => {
    const { ref } = useContext(FieldHighlighterProviderContext);
    const refOnly = useCallback((theRef) => {
        ref(theRef, tooltipButtons);
    }, [tooltipButtons, ref]);
    return {
        ref: refOnly
    };
};
export default FieldHighlighterProvider;
